import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";

export default function EditSong() {
  const { id } = useParams();
  const navigate = useNavigate();

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userId = user?._id;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    lyrics: "",
    category: "",
    tags: "",
  });

  const [genres, setGenres] = useState([]); // Dropdown ke liye
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [currentThumbnail, setCurrentThumbnail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  /* ================= FETCH DATA (Genres + Song) ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Fetch Genres for dropdown
        const genRes = await api.get("/client/song/getGenres");
        if (genRes.data.status) setGenres(genRes.data.genres);

        // 2. Fetch User's Songs to find this specific one
        const res = await api.get("/client/song/songsOfChannel", {
          params: { channelId: user?.channelId },
        });

        if (res.data.status) {
          const song = res.data.songs.find((s) => s._id === id);
          if (song) {
            setFormData({
              title: song.title || "",
              description: song.description || "",
              lyrics: song.lyrics || "",
              category: song.category || "",
              tags: song.tags?.join(", ") || "",
            });
            setCurrentThumbnail(song.thumbnailUrl);
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.channelId) fetchData();
  }, [id, user?.channelId]);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= HANDLE UPDATE ================= */
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) return alert("Song title is required!");

    try {
      setUpdating(true);
      const form = new FormData();
      form.append("songId", id);
      form.append("userId", userId);
      form.append("title", formData.title);
      form.append("description", formData.description);
      form.append("lyrics", formData.lyrics);
      form.append("category", formData.category);
      form.append("tags", formData.tags);

      // Backend expects "thumbnail" key based on your multer config
      if (thumbnailFile) {
        form.append("thumbnail", thumbnailFile);
      }

      const res = await api.patch("/client/song/updateSong", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status) {
        alert("Song updated successfully!");
        navigate("/user/content");
      } else {
        alert(res.data.message || "Update failed");
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Error connecting to server");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="panel-loading">Loading Song Details...</div>;

  return (
    <div className="panel-container">
      <div className="edit-header">
        <h2>Edit Song: <span style={{color: '#888'}}>{formData.title}</span></h2>
      </div>

      <div className="edit-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
        
        {/* LEFT SIDE: FORMS */}
        <div className="edit-form-section">
          <div className="form-group">
            <label>Song Title*</label>
            <input
              type="text"
              name="title"
              className="form-input"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter song title"
            />
          </div>

          <div className="form-group">
            <label>Category / Genre</label>
            <select 
              name="category" 
              className="form-input" 
              value={formData.category} 
              onChange={handleChange}
            >
              <option value="">Select Category</option>
              {genres.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              className="form-textarea"
              value={formData.description}
              onChange={handleChange}
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Lyrics</label>
            <textarea
              name="lyrics"
              className="form-textarea"
              value={formData.lyrics}
              onChange={handleChange}
              rows={10}
              placeholder="Paste song lyrics here..."
            />
          </div>

          <div className="form-group">
            <label>Tags (Separate with commas)</label>
            <input
              type="text"
              name="tags"
              className="form-input"
              value={formData.tags}
              onChange={handleChange}
              placeholder="pop, romantic, 2024"
            />
          </div>

          <div className="action-btns" style={{ marginTop: '20px' }}>
            <button 
              className="primary-btn" 
              onClick={handleUpdate} 
              disabled={updating}
            >
              {updating ? "Saving..." : "Save Changes"}
            </button>
            <button 
              className="secondary-btn" 
              onClick={() => navigate(-1)} 
              style={{ marginLeft: "10px" }}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* RIGHT SIDE: THUMBNAIL */}
        <div className="edit-media-section">
          <div className="form-group">
            <label>Song Thumbnail</label>
            <div className="thumbnail-preview" style={{ marginBottom: '10px' }}>
              <img
                src={thumbnailFile ? URL.createObjectURL(thumbnailFile) : currentThumbnail}
                alt="preview"
                style={{ width: "100%", aspectSratio: '1/1', objectFit: 'cover', borderRadius: "12px", border: '1px solid #ddd' }}
              />
            </div>
            <input
              type="file"
              id="thumb-upload"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => setThumbnailFile(e.target.files[0])}
            />
            <label htmlFor="thumb-upload" className="secondary-btn" style={{ display: 'block', textAlign: 'center', cursor: 'pointer' }}>
              Change Image
            </label>
          </div>
        </div>

      </div>
    </div>
  );
}